import type { ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { type IEditable, type IEditCellChange, isCellEditable } from "../../../redux/modules/data-editing.js";
import { type ITableColumn } from "../../../redux/modules/table-data.js";
import InMemoryTableWithSelectionModel, {
  type IInMemoryTableWithSelectionModelProps,
} from "../../core/grid/InMemoryTableWithSelectionModel.js";

interface IEditingGridProps {
  columns: ITableColumn[];
  rowData: IEditable[];
  isLoading: boolean;
  setEditingGridChanges: (changes: IEditCellChange[]) => void;
  tableProps?: Partial<IInMemoryTableWithSelectionModelProps>;
  showLockedColumn?: boolean;
}

/**
 * Screen with EditTable for modification of Phenotype data
 */
const EditingGridScreen: FC<IEditingGridProps> = (props) => {
  const [editableColumns, setEditableColumns] = useState<ITableColumn[]>(null);

  const editCallback = useCallback(
    (v: ValueSetterParams, column: Partial<ITableColumn>) => {
      const change: IEditCellChange = {
        rowId: v.data.id,
        newValue: typeof v.newValue === "number" ? String(v.newValue) : v.newValue,
        column: column,
      };
      props.setEditingGridChanges([change]);
      return true;
    },
    [props.setEditingGridChanges],
  );

  useEffect(() => {
    const editableColumns: ITableColumn[] = props.columns.map((c) => {
      return {
        ...c,
        // cell level editing needs to be mapped
        editable: c.editable && isCellEditable,
        valueGetter: (params: ValueGetterParams) => {
          return params.data[params.colDef.colId]; // need colId to be customized for this to work (grid.ts) since colId is usually set to "field" downstream
        },
        valueSetter: (v: ValueSetterParams) => editCallback(v, c),
      } satisfies ITableColumn;
    });
    setEditableColumns(editableColumns);
  }, [props.columns, editCallback]);

  return (
    <InMemoryTableWithSelectionModel
      columns={editableColumns}
      rowGridData={props.rowData}
      useSelectionModel={false}
      showLockedColumn={props.showLockedColumn}
      uniqueGridIdentifier={"edit-data"}
      gridContext={{ editMode: true, errorMode: useCountInvalidCells(props.rowData) > 0 }}
      isLoading={props.isLoading}
      editingTheme
      gridProps={{
        stopEditingWhenCellsLoseFocus: false,
      }}
      {...(props.tableProps || {})}
    />
  );
};

const useCountInvalidCells = (dataGrid: IEditable[]) => {
  const [invalidCellsCount, setInvalidCellsCount] = useState<number>(0);

  useMemo(() => {
    let count = 0;

    dataGrid
      .filter((data: IEditable) => data.editing)
      .forEach((data: IEditable) => {
        Object.keys(data.editing).forEach((columnKey: string) => {
          if (data.editing[columnKey]?.validationError) {
            count++;
          }
        });
      });
    setInvalidCellsCount(count);
  }, [dataGrid]);
  return invalidCellsCount;
};

export { useCountInvalidCells };
export default EditingGridScreen;
